/**
 * AchatsToolbar — Recherche live + filtres (fournisseur, catégorie, destination, mode paiement, période)
 *
 * Reçoit toutes les expenses et retourne la liste filtrée via onFilter.
 * Stateless côté parent → toute la logique de filtre est locale.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Filter, X, RotateCcw } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const CATEGORIES = ["cuisine", "bar", "salle", "jeux_vr", "jardin", "administratif", "toilettes", "autres"];
const DESTS = ["cuisine", "bar", "salle", "jeux_vr", "jardin", "administratif"];
const PAY_MODES = [
  { v: "fonds_propres", label: "Fonds propres" },
  { v: "caisse_restau", label: "Caisse restaurant" },
];

export default function AchatsToolbar({ expenses = [], onFilter }) {
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("");
  const [category, setCategory] = useState("");
  const [destination, setDestination] = useState("");
  const [payMode, setPayMode] = useState("");
  const [suppliersOpts, setSuppliersOpts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    axios.get(`${API}/expenses/suppliers`)
      .then(r => setSuppliersOpts(r.data?.suppliers || []))
      .catch(() => setSuppliersOpts([]));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter(e => {
      if (q) {
        const hay = [
          e.description, e.supplier, e.category, e.destination,
          ...(e.items || []).map(it => it.description),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (supplier && (e.supplier || "").toLowerCase() !== supplier.toLowerCase()) return false;
      if (category && (e.category || "").toLowerCase() !== category) return false;
      if (destination && (e.destination || "").toLowerCase() !== destination) return false;
      if (payMode && (e.payment_mode || "") !== payMode) return false;
      return true;
    });
  }, [expenses, search, supplier, category, destination, payMode]);

  useEffect(() => { onFilter?.(filtered); }, [filtered, onFilter]);

  const reset = () => {
    setSearch(""); setSupplier(""); setCategory(""); setDestination(""); setPayMode("");
  };
  const activeCount =
    (supplier ? 1 : 0) + (category ? 1 : 0) + (destination ? 1 : 0) + (payMode ? 1 : 0);

  return (
    <div className="space-y-1.5" data-testid="achats-toolbar">
      <div className="flex items-center gap-1.5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher description, article, fournisseur…"
            className="w-full h-9 pl-8 pr-3 rounded-lg bg-slate-900/70 border border-slate-700 text-slate-200 text-[12.5px] placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/60"
            data-testid="achats-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white p-0.5"
              title="Effacer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFilters(s => !s)}
          className={`h-9 px-3 rounded-lg font-bold text-[12px] flex items-center gap-1.5 border transition-colors ${
            activeCount > 0
              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
              : "bg-slate-900/70 text-slate-300 border-slate-700 hover:border-slate-600"
          }`}
          data-testid="achats-toggle-filters"
        >
          <Filter className="w-3.5 h-3.5" strokeWidth={2.4} />
          Filtres
          {activeCount > 0 && (
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-slate-900 text-[9.5px] font-black">{activeCount}</span>
          )}
        </button>
        {(search || activeCount > 0) && (
          <button
            type="button"
            onClick={reset}
            className="h-9 px-3 rounded-lg text-slate-400 hover:text-white text-[12px] font-semibold flex items-center gap-1"
            title="Réinitialiser"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>

      {showFilters && (
        <div className="p-2.5 rounded-lg bg-slate-900/50 border border-slate-700/50 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="h-8 px-2 rounded-md bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px]"
            data-testid="achats-filter-supplier"
          >
            <option value="">Tous fournisseurs</option>
            {suppliersOpts.map(s => (
              <option key={s.name} value={s.name}>
                {s.name.slice(0, 28)} · {s.count}
              </option>
            ))}
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-8 px-2 rounded-md bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px]"
            data-testid="achats-filter-category"
          >
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            className="h-8 px-2 rounded-md bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px]"
            data-testid="achats-filter-destination"
          >
            <option value="">Toutes destinations</option>
            {DESTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={payMode}
            onChange={(e) => setPayMode(e.target.value)}
            className="h-8 px-2 rounded-md bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px]"
            data-testid="achats-filter-paymode"
          >
            <option value="">Tout mode paiement</option>
            {PAY_MODES.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
